import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';

import { useLocation, useNavigate } from 'react-router-dom';

import PageLoader from '../../../components/ui/PageLoader';

import { useResponsive } from '../../../context/ResponsiveContext';

import { useMediaQuery } from '../../../hooks/useMediaQuery';

import { Z_CLASS } from '../../../lib/z-index';

import { cn } from '../../../lib/utils';

import { useNavUi } from '../../../components/layout/navUiContext';

import {

  isChapterBreakStep,

  isManualNavigationDuringTour,

  prepareStepForDisplay,

  resolveStepRoute,

} from '../walkthroughOrchestrator';

import { useWalkthrough } from '../walkthroughContext';

import { WalkthroughSpotlight } from './WalkthroughSpotlight';

import { WalkthroughTooltip } from './WalkthroughTooltip';

import { resolveTargetSelector } from './walkthroughPosition';
import { subscribeWalkthroughLayoutChange } from './walkthroughLayout';



/**

 * @param {import('../walkthroughTypes').ResolvedWalkthroughStep} step

 * @returns {boolean}

 */

function isModalStep(step) {

  return step.placement === 'modal' || isChapterBreakStep(step) || !resolveTargetSelector(step, false);

}



/**

 * Guided walkthrough overlay — spotlight, tooltip, keyboard, and focus management.

 */

export function WalkthroughOverlay() {

  const {

    isActive,

    isPaused,

    isNavigating,

    expectedRoute,

    currentStep,

    stepIndex,

    stepCount,

    next,

    prev,

    skip,

    complete,

    skipCurrentStep,

    skipChapter,

    resumeTour,

    pauseTour,

    setNavigating,

    setExpectedRoute,

  } = useWalkthrough();

  const { openMobileNav, isMobile: isCompactNav, expandSidebar, isSidebarCollapsed } =
    useNavUi();

  const { isMobile: isNarrowViewport } = useResponsive();

  const location = useLocation();
  const navigate = useNavigate();

  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const liveRegionId = useId();

  const tooltipContainerRef = useRef(null);

  const previouslyFocusedRef = useRef(null);

  const prepareAbortRef = useRef(null);

  const isPreparingStepRef = useRef(false);

  const expectedRouteRef = useRef(expectedRoute);

  expectedRouteRef.current = expectedRoute;

  const wasPausedRef = useRef(false);

  const prevStepIdRef = useRef(null);

  const [targetElement, setTargetElement] = useState(null);

  const [targetRect, setTargetRect] = useState(null);

  const [isPreparingStep, setIsPreparingStep] = useState(false);



  const resolveTarget = useCallback(() => {

    if (!currentStep) {

      setTargetElement(null);

      setTargetRect(null);

      return;

    }



    const selector = resolveTargetSelector(currentStep, isCompactNav);

    if (!selector || isModalStep(currentStep)) {

      setTargetElement(null);

      setTargetRect(null);

      return;

    }



    const el = document.querySelector(selector);

    setTargetElement(el instanceof HTMLElement ? el : null);

    setTargetRect(el instanceof HTMLElement ? el.getBoundingClientRect() : null);

  }, [currentStep, isCompactNav]);



  useEffect(() => {

    if (!isActive || !currentStep) {

      return undefined;

    }



    if (isPaused) {

      wasPausedRef.current = true;

      return undefined;

    }



    const justResumed = wasPausedRef.current;

    wasPausedRef.current = false;



    const stepRoute = resolveStepRoute(currentStep);

    const stepChanged = prevStepIdRef.current !== currentStep.id;

    prevStepIdRef.current = currentStep.id;



    if (

      !justResumed &&

      !stepChanged &&

      isManualNavigationDuringTour(
        stepRoute ?? expectedRouteRef.current,
        location.pathname,
        isNavigating || isPreparingStepRef.current
      )

    ) {

      pauseTour('manual-navigation');

      return undefined;

    }



    prepareAbortRef.current?.abort();

    const controller = new AbortController();

    prepareAbortRef.current = controller;

    let cancelled = false;

    isPreparingStepRef.current = true;
    setIsPreparingStep(true);



    (async () => {



      const result = await prepareStepForDisplay({

        step: currentStep,

        currentPath: location.pathname,

        navigate,

        isMobile: isCompactNav,

        openMobileNav,

        expandSidebar,

        isSidebarCollapsed,

        onNavigating: setNavigating,

        signal: controller.signal,

      });



      if (cancelled || controller.signal.aborted) {

        return;

      }



      setExpectedRoute((prev) =>
        prev === result.expectedRoute ? prev : result.expectedRoute
      );



      if (result.skipped) {

        skipCurrentStep();

        return;

      }



      if (result.target) {

        setTargetElement(result.target);

        setTargetRect(result.target.getBoundingClientRect());

      } else {

        resolveTarget();

      }

    })().finally(() => {

      if (!cancelled) {

        isPreparingStepRef.current = false;
        setIsPreparingStep(false);

      }

    });



    return () => {

      cancelled = true;

      controller.abort();

    };

  }, [

    currentStep,

    isActive,

    isCompactNav,

    expandSidebar,

    isSidebarCollapsed,

    isNavigating,

    isPaused,

    location.pathname,

    navigate,

    openMobileNav,

    pauseTour,

    resolveTarget,

    setExpectedRoute,

    setNavigating,

    skipCurrentStep,

  ]);



  useEffect(() => {

    if (!targetElement) return undefined;



    const syncRect = () => {

      setTargetRect(targetElement.getBoundingClientRect());

    };



    window.addEventListener('resize', syncRect);

    window.addEventListener('orientationchange', syncRect);

    document.addEventListener('scroll', syncRect, true);

    const unsubscribeLayout = subscribeWalkthroughLayoutChange(syncRect);



    return () => {

      window.removeEventListener('resize', syncRect);

      window.removeEventListener('orientationchange', syncRect);

      document.removeEventListener('scroll', syncRect, true);

      unsubscribeLayout();

    };

  }, [targetElement]);



  useEffect(() => {

    if (!isActive || !isNarrowViewport || !targetElement || isPaused) return;



    targetElement.scrollIntoView({

      block: 'center',

      behavior: prefersReducedMotion ? 'auto' : 'smooth',

    });

  }, [isActive, isNarrowViewport, isPaused, targetElement, currentStep?.id, prefersReducedMotion]);



  useEffect(() => {

    if (!isActive || isPaused) return undefined;



    previouslyFocusedRef.current = document.activeElement;



    return () => {

      const prev = previouslyFocusedRef.current;

      if (prev instanceof HTMLElement && document.contains(prev)) {

        prev.focus();

      }

    };

  }, [isActive, isPaused]);



  const handleBack = useCallback(() => {

    prev();

  }, [prev]);



  const handleNext = useCallback(() => {

    if (stepIndex >= stepCount - 1) {

      complete();

      return;

    }

    next();

  }, [complete, next, stepCount, stepIndex]);



  const handleSkip = useCallback(() => {

    skip();

  }, [skip]);



  const handleSkipChapter = useCallback(() => {

    skipChapter();

  }, [skipChapter]);



  const handleDialogOpenChange = useCallback((open) => {

    // Tour stays open until Skip / End tour — ignore Radix dismiss requests.

    void open;

  }, []);



  useEffect(() => {

    if (!isActive || isPaused) return undefined;



    const handleKeyDown = (event) => {

      if (event.key === 'ArrowRight') {

        event.preventDefault();

        handleNext();

        return;

      }

      if (event.key === 'ArrowLeft' && stepIndex > 0) {

        event.preventDefault();

        handleBack();

      }

    };



    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);

  }, [handleBack, handleNext, isActive, isPaused, stepIndex]);



  if (!isActive || !currentStep) {

    return null;

  }



  const showTransitionLoader = isNavigating || isPreparingStep;

  const chapterBreak = isChapterBreakStep(currentStep);

  const isFirst = stepIndex === 0;

  const isLast = stepIndex === stepCount - 1;

  const modalStep = isModalStep(currentStep);

  const showSpotlight = !isPaused && currentStep.placement !== 'modal' && !!targetElement;



  if (isPaused) {

    return (

      <div

        className={cn(

          Z_CLASS.WALKTHROUGH,

          'pointer-events-auto fixed inset-x-0 top-0 safe-area-left safe-area-right'

        )}

      >

        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 border-b border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 shadow-md dark:border-amber-700 dark:bg-amber-950 dark:text-amber-50">

          <p>Tour paused — you navigated away. Resume to return to the current step, or end the tour.</p>

          <div className="flex shrink-0 items-center gap-2">

            <button

              type="button"

              className="rounded-md border border-amber-400 bg-white px-3 py-1.5 font-medium text-amber-950 hover:bg-amber-100 dark:border-amber-600 dark:bg-amber-900 dark:text-amber-50 dark:hover:bg-amber-800"

              onClick={skip}

            >

              End tour

            </button>

            <button

              type="button"

              className="rounded-md bg-amber-600 px-3 py-1.5 font-medium text-white hover:bg-amber-700"

              onClick={resumeTour}

            >

              Resume tour

            </button>

          </div>

        </div>

      </div>

    );

  }



  return (

    <DialogPrimitive.Root open onOpenChange={handleDialogOpenChange}>

      <DialogPrimitive.Portal>

        <div className={cn(Z_CLASS.WALKTHROUGH, 'fixed inset-0')}>

          {showTransitionLoader ? (

            <div className="pointer-events-auto fixed inset-0 z-[1] bg-black/35">

              <PageLoader />

            </div>

          ) : null}



          <div

            className={cn(

              'pointer-events-none fixed inset-0',

              !showSpotlight && 'bg-black/55'

            )}

            aria-hidden="true"

          />



          <WalkthroughSpotlight

            targetElement={showSpotlight ? targetElement : null}

            visible={showSpotlight}

          />



          <WalkthroughTooltip

            key={currentStep.id}

            containerRef={tooltipContainerRef}

            title={currentStep.title}

            body={currentStep.body}

            stepIndex={stepIndex}

            stepCount={stepCount}

            isFirst={isFirst}

            isLast={isLast}

            isMobile={isNarrowViewport}

            isModal={modalStep}

            isChapterBreak={chapterBreak}

            isOptionalChapter={chapterBreak && !!currentStep.optional}

            targetRect={targetRect}

            placement={currentStep.placement}

            onBack={handleBack}

            onNext={handleNext}

            onSkip={handleSkip}

            onSkipChapter={handleSkipChapter}

          />



          <div

            id={liveRegionId}

            aria-live="polite"

            aria-atomic="true"

            className="sr-only"

          >

            {`Step ${stepIndex + 1} of ${stepCount}: ${currentStep.title}`}

          </div>

        </div>

      </DialogPrimitive.Portal>

    </DialogPrimitive.Root>

  );

}



export default WalkthroughOverlay;


